import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Drawer,
  Group,
  NumberInput,
  Stack,
  Switch,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { isAxiosError } from 'axios';
import type { ModifierGroupDto } from './types';
import { useCreateModifierGroup, useUpdateModifierGroup } from './modifierGroupsApi';

interface Props {
  opened: boolean;
  onClose: () => void;
  group: ModifierGroupDto | null;
}

interface FormValues {
  nameAr: string;
  nameEn: string;
  minSelections: number;
  maxSelections: number;
  isRequired: boolean;
  sortOrder: number;
}

export function ModifierGroupFormDrawer({ opened, onClose, group }: Props) {
  const { t, i18n } = useTranslation();
  const create = useCreateModifierGroup();
  const update = useUpdateModifierGroup();
  const submitting = create.isPending || update.isPending;
  const editing = group !== null;
  const drawerPosition = i18n.language === 'ar' ? 'left' : 'right';

  const form = useForm<FormValues>({
    initialValues: {
      nameAr: '',
      nameEn: '',
      minSelections: 0,
      maxSelections: 0,
      isRequired: false,
      sortOrder: 0,
    },
    validate: {
      nameAr: (v) => (v.trim() ? null : t('common.errors.required')),
      minSelections: (v) => (v < 0 ? t('modifierGroups.form.minNegative') : null),
      maxSelections: (v, vals) =>
        v < 0
          ? t('modifierGroups.form.maxNegative')
          : v > 0 && v < vals.minSelections
            ? t('modifierGroups.form.maxLessThanMin')
            : null,
    },
  });

  useEffect(() => {
    if (!opened) return;
    form.setValues({
      nameAr: group?.nameAr ?? '',
      nameEn: group?.nameEn ?? '',
      minSelections: group?.minSelections ?? 0,
      maxSelections: group?.maxSelections ?? 0,
      isRequired: group?.isRequired ?? false,
      sortOrder: group?.sortOrder ?? 0,
    });
    form.resetDirty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, group]);

  const submit = async (values: FormValues) => {
    const payload = {
      nameAr: values.nameAr.trim(),
      nameEn: values.nameEn.trim() || null,
      minSelections: values.minSelections,
      maxSelections: values.maxSelections,
      isRequired: values.isRequired,
      sortOrder: values.sortOrder,
    };
    try {
      if (editing) {
        await update.mutateAsync({ id: group.modifierGroupId, request: payload });
      } else {
        await create.mutateAsync(payload);
      }
      notifications.show({
        color: 'green',
        title: editing ? t('common.toasts.updated') : t('common.toasts.created'),
        message: payload.nameAr,
      });
      onClose();
    } catch (err) {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: extractApiMessage(err) ?? t('common.toasts.errorBody'),
      });
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={editing ? t('modifierGroups.form.editTitle') : t('modifierGroups.form.createTitle')}
      position={drawerPosition}
      size="md"
      padding="lg"
    >
      <form onSubmit={form.onSubmit(submit)}>
        <Stack gap="md">
          <TextInput
            label={t('modifierGroups.form.nameArLabel')}
            placeholder={t('modifierGroups.form.nameArPlaceholder')}
            withAsterisk
            {...form.getInputProps('nameAr')}
          />
          <TextInput
            label={t('modifierGroups.form.nameEnLabel')}
            placeholder={t('modifierGroups.form.nameEnPlaceholder')}
            {...form.getInputProps('nameEn')}
          />
          <Group grow align="flex-start">
            <NumberInput
              label={t('modifierGroups.form.minLabel')}
              description={t('modifierGroups.form.minHelp')}
              min={0}
              step={1}
              {...form.getInputProps('minSelections')}
            />
            <NumberInput
              label={t('modifierGroups.form.maxLabel')}
              description={t('modifierGroups.form.maxHelp')}
              min={0}
              step={1}
              {...form.getInputProps('maxSelections')}
            />
          </Group>
          <Switch
            label={t('modifierGroups.form.requiredLabel')}
            description={t('modifierGroups.form.requiredHelp')}
            checked={form.values.isRequired}
            onChange={(e) => form.setFieldValue('isRequired', e.currentTarget.checked)}
          />
          <NumberInput
            label={t('modifierGroups.form.sortOrderLabel')}
            description={t('modifierGroups.form.sortOrderHelp')}
            min={0}
            step={1}
            {...form.getInputProps('sortOrder')}
          />

          <Group justify="flex-end" gap="sm" mt="sm">
            <Button variant="default" onClick={onClose} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={submitting}>
              {editing ? t('common.save') : t('common.create')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}

function extractApiMessage(err: unknown): string | null {
  if (isAxiosError(err)) {
    const data = err.response?.data as { detail?: string; title?: string } | undefined;
    return data?.detail ?? data?.title ?? err.message;
  }
  return null;
}
